import React, { useState } from 'react';
import { collection, addDoc, updateDoc, query, where, getDocs, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { useUser } from '../context/UserContext';

interface MigrationLog {
  timestamp: Date;
  type: 'info' | 'success' | 'warning' | 'error';
  message: string;
}

export default function AMPIMigration() {
  const { user } = useUser();
  const [isRunning, setIsRunning] = useState(false);
  const [logs, setLogs] = useState<MigrationLog[]>([]);
  const [results, setResults] = useState<any>(null);
  const [csvAgencias, setCsvAgencias] = useState<string>('');
  const [csvMiembros, setCsvMiembros] = useState<string>('');

  // Solo admins pueden acceder
  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Acceso Denegado</h4>
          <p>Solo administradores pueden ejecutar la migración AMPI.</p>
        </div>
      </div>
    );
  }

  const addLog = (type: 'info' | 'success' | 'warning' | 'error', message: string) => {
    setLogs(prev => [...prev, {
      timestamp: new Date(),
      type,
      message
    }]);
  };

  const handleFileRead = (file: File, setterCallback: (content: string) => void) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setterCallback(content);
    };
    reader.readAsText(file, 'UTF-8');
  };

  // Función mejorada para parsear CSV complejos
  const parseCSV = (csvText: string) => {
    const lines = csvText.split('\n');
    if (lines.length === 0) return [];
    
    // Procesar headers
    const headerLine = lines[0];
    const headers = parseCSVLine(headerLine);
    console.log('Headers encontrados:', headers);
    
    const data: any[] = [];
    let currentRowData: string[] = [];
    let inQuotes = false;
    let currentCell = '';
    
    // Procesar línea por línea, manejando saltos de línea dentro de celdas
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      
      // Saltar líneas completamente vacías
      if (!line.trim() && !inQuotes) continue;
      
      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          currentRowData.push(currentCell.trim());
          currentCell = '';
        } else {
          currentCell += char;
        }
      }
      
      // Si llegamos al final de la línea
      if (!inQuotes) {
        // Agregar última celda
        currentRowData.push(currentCell.trim());
        
        // Si tenemos el número correcto de columnas, crear el objeto
        if (currentRowData.length === headers.length && currentRowData.some(cell => cell.length > 0)) {
          const row: any = {};
          headers.forEach((header, index) => {
            row[header] = currentRowData[index] || '';
          });
          data.push(row);
        }
        
        // Resetear para próxima fila
        currentRowData = [];
        currentCell = '';
      } else {
        // Si estamos en quotes, agregar salto de línea y continuar
        currentCell += '\n';
      }
    }
    
    console.log(`Procesadas ${data.length} filas de CSV`);
    return data;
  };

  // Helper para parsear una línea de CSV con quotes
  const parseCSVLine = (line: string): string[] => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  };

  // Generadores de ID únicos
  const generarInmobiliariaId = (): string => {
    const letters = 'abcdefghijklmnopqrstuvwxyz';
    const letra = letters[Math.floor(Math.random() * letters.length)];
    const numero = Math.floor(100000 + Math.random() * 900000);
    return `${letra}${numero}`;
  };

  const generarAsesorId = (): string => {
    const letters = 'abcdefghijklmnopqrstuvwxyz';
    const letra1 = letters[Math.floor(Math.random() * letters.length)];
    const letra2 = letters[Math.floor(Math.random() * letters.length)];
    const numero = Math.floor(10000 + Math.random() * 90000);
    return `${letra1}${letra2}${numero}`;
  };

  const generarAsociadoId = (): string => {
    const letters = 'abcdefghijklmnopqrstuvwxyz';
    const letra1 = letters[Math.floor(Math.random() * letters.length)];
    const letra2 = letters[Math.floor(Math.random() * letters.length)]; 
    const letra3 = letters[Math.floor(Math.random() * letters.length)];
    const numero = Math.floor(10000 + Math.random() * 90000);
    return `${letra1}${letra2}${letra3}${numero}`;
  };

  const ejecutarMigracion = async () => {
    if (!csvAgencias || !csvMiembros) {
      addLog('error', 'Debes cargar ambos archivos CSV antes de continuar');
      return;
    }

    setIsRunning(true);
    setLogs([]);
    setResults(null);

    try {
      addLog('info', '🚀 Iniciando migración AMPI completa...');
      
      // FASE 1: Procesar agencias
      addLog('info', '📍 FASE 1: Procesando agencias...');
      const agenciasData = parseCSV(csvAgencias);
      const agenciasCreadas = new Map<string, string>();
      const resultadosAgencias = [];

      for (const agencia of agenciasData) {
        if (!agencia.Agencia || agencia.Agencia.trim() === '') continue;
        
        try {
          const inmobiliaria_id = generarInmobiliariaId();
          
          const inmobiliariaDoc = {
            inmobiliaria_id,
            nombre: agencia.Agencia?.trim() || '',
            descripcion: agencia.Descripción || '',
            correo: (agencia.CORREO || '').toLowerCase().trim(),
            telefono: agencia.TELÉFONO || '',
            direccion: agencia.Address || '',
            sitio_web: agencia['Sitio Web'] || '',
            redes: {
              facebook: agencia.FACEBOOK || '',
              instagram: agencia.INSTAGRAM || ''
            },
            anos_experiencia: 0,
            
            // Arrays vacíos inicialmente
            asesor_id: [],
            asociado_id: [],
            propiedad_id: [],
            proyecto_id: [],
            
            status: 'ACTIVE',
            fecha_creacion: serverTimestamp()
          };
          
          const docRef = await addDoc(collection(db, 'inmobiliaria'), inmobiliariaDoc);
          agenciasCreadas.set(agencia.Agencia.trim(), inmobiliaria_id);
          
          resultadosAgencias.push({
            success: true,
            inmobiliaria_id,
            nombre: inmobiliariaDoc.nombre,
            firestore_id: docRef.id
          });
          
          addLog('success', `✅ Agencia creada: ${inmobiliariaDoc.nombre}`);
          
        } catch (error: any) {
          addLog('error', `❌ Error creando agencia ${agencia.Agencia}: ${error.message}`);
          resultadosAgencias.push({
            success: false,
            nombre: agencia.Agencia,
            error: error.message
          });
        }
      }

      // FASE 2: Procesar miembros
      addLog('info', '📍 FASE 2: Procesando miembros...');
      const miembrosData = parseCSV(csvMiembros);
      const resultadosMiembros = [];

      for (const miembro of miembrosData) {
        if (!miembro.Nombre || miembro.Nombre.trim() === '') continue;
        
        try {
          const esAsesor = miembro.STATUS === 'Afiliado';
          const esAsociado = miembro.STATUS === 'Asociado';
          
          if (!esAsesor && !esAsociado) {
            addLog('warning', `⚠️ STATUS desconocido: ${miembro.Nombre} - ${miembro.STATUS}`);
            continue;
          }
          
          // Búsqueda mejorada de agencia (exacta y fuzzy)
          let inmobiliaria_id = agenciasCreadas.get(miembro.Agencia?.trim());
          
          // Si no encontramos coincidencia exacta, buscar coincidencias parciales
          if (!inmobiliaria_id) {
            const agenciaBuscada = miembro.Agencia?.trim().toLowerCase();
            for (const [nombreAgencia, id] of Array.from(agenciasCreadas.entries())) {
              if (nombreAgencia.toLowerCase().includes(agenciaBuscada) || 
                  agenciaBuscada.includes(nombreAgencia.toLowerCase())) {
                inmobiliaria_id = id;
                addLog('info', `🔗 Vinculación fuzzy: ${miembro.Agencia} → ${nombreAgencia}`);
                break;
              }
            }
          }
          
          if (!inmobiliaria_id) {
            addLog('warning', `⚠️ No se encontró agencia para: ${miembro.Nombre} → ${miembro.Agencia}`);
            resultadosMiembros.push({
              success: false,
              nombre: miembro.Nombre,
              agencia: miembro.Agencia,
              error: 'Agencia no encontrada'
            });
            continue;
          }
          
          const miembroId = esAsesor ? generarAsesorId() : generarAsociadoId();
          const collectionName = esAsesor ? 'asesor' : 'asociado';
          const idField = esAsesor ? 'asesor_id' : 'asociado_id';
          
          const miembroDoc = {
            [idField]: miembroId,
            nombre: miembro.Nombre.trim(),
            correo: (miembro.Correo || '').toLowerCase().trim(),
            telefono: miembro.Telefono || '',
            tipo: esAsesor ? 'asesor' : 'asociado',
            inmobiliaria_id: esAsesor ? [inmobiliaria_id] : inmobiliaria_id,
            sitio_web: miembro['Sitio web'] || '',
            
            activo: true,
            anos_experiencia: 0,
            biografia: '',
            breakdown_rating: {
              cursos: 0,
              experiencia: 0,
              opiniones: 0,
              participacion: 0,
              perfil: 0
            },
            calificacion: 0,
            certificaciones: [],
            nivel_calificacion: 'Nuevo',
            propiedad_id: [],
            redes: {
              facebook: '',
              instagram: ''
            },
            fecha_creacion: serverTimestamp()
          };
          
          const docRef = await addDoc(collection(db, collectionName), miembroDoc);
          
          // Actualizar array en inmobiliaria
          const arrayField = esAsesor ? 'asesor_id' : 'asociado_id';
          const q = query(collection(db, 'inmobiliaria'), where('inmobiliaria_id', '==', inmobiliaria_id));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const inmobiliariaDocRef = querySnapshot.docs[0].ref;
            await updateDoc(inmobiliariaDocRef, {
              [arrayField]: arrayUnion(miembroId)
            });
          }
          
          resultadosMiembros.push({
            success: true,
            miembro_id: miembroId,
            nombre: miembro.Nombre,
            tipo: miembro.STATUS,
            agencia: miembro.Agencia,
            inmobiliaria_id,
            firestore_id: docRef.id,
            email: miembro.Correo
          });
          
          addLog('success', `✅ ${miembro.STATUS} creado: ${miembro.Nombre}`);
          
        } catch (error: any) {
          addLog('error', `❌ Error creando miembro ${miembro.Nombre}: ${error.message}`);
          resultadosMiembros.push({
            success: false,
            nombre: miembro.Nombre,
            agencia: miembro.Agencia,
            error: error.message
          });
        }
      }

      // FASE 3: Crear cuentas Firebase (opcional - solo para emails válidos)
      addLog('info', '📍 FASE 3: Creando cuentas Firebase...');
      const miembrosExitosos = resultadosMiembros.filter(m => m.success);
      const miembrosConEmail = miembrosExitosos.filter(m => m.email && m.email.includes('@') && m.email.length > 5);
      
      addLog('info', `📧 ${miembrosConEmail.length} miembros tienen emails válidos de ${miembrosExitosos.length} exitosos`);
      
      const resultadosFirebase = [];

      for (const miembro of miembrosConEmail) {
        try {
          const email = miembro.email.toLowerCase().trim();
          const password = Math.random().toString(36).slice(-8) + Math.random().toString(36).slice(-8);
          
          addLog('info', `🔐 Creando cuenta Firebase para: ${miembro.nombre} (${email})`);
          
          const userCredential = await createUserWithEmailAndPassword(auth, email, password);
          const uid = userCredential.user.uid;
          
          // Actualizar documento del miembro con UID
          const collectionName = miembro.tipo === 'Afiliado' ? 'asesor' : 'asociado';
          const idField = miembro.tipo === 'Afiliado' ? 'asesor_id' : 'asociado_id';
          
          const q = query(collection(db, collectionName), where(idField, '==', miembro.miembro_id));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const miembroDocRef = querySnapshot.docs[0].ref;
            await updateDoc(miembroDocRef, {
              firebase_uid: uid
            });
            addLog('success', `🔗 UID vinculado en ${collectionName}: ${miembro.miembro_id}`);
          }
          
          resultadosFirebase.push({
            success: true,
            nombre: miembro.nombre,
            email: email,
            uid,
            password,
            miembro_id: miembro.miembro_id
          });
          
          addLog('success', `✅ Cuenta Firebase: ${miembro.nombre} (${email})`);
          
        } catch (error: any) {
          addLog('error', `❌ Firebase Auth error: ${miembro.nombre} - ${error.message}`);
          resultadosFirebase.push({
            success: false,
            nombre: miembro.nombre,
            email: miembro.email,
            error: error.message
          });
        }
      }

      const resultado = {
        agencias: {
          total: resultadosAgencias.length,
          exitosas: resultadosAgencias.filter(r => r.success).length,
          fallidas: resultadosAgencias.filter(r => !r.success)
        },
        miembros: {
          total: resultadosMiembros.length,
          exitosos: resultadosMiembros.filter(r => r.success).length,
          fallidos: resultadosMiembros.filter(r => !r.success)
        },
        cuentas_firebase: {
          creadas: resultadosFirebase.filter(r => r.success).length,
          fallidas: resultadosFirebase.filter(r => !r.success)
        }
      };
      
      setResults(resultado);
      addLog('success', '🎉 Migración completada exitosamente!');
      
      // Mostrar passwords generadas
      const passwordsCreadas = resultadosFirebase.filter(r => r.success);
      if (passwordsCreadas.length > 0) {
        addLog('info', '🔑 Passwords temporales generadas:');
        passwordsCreadas.forEach(cuenta => {
          addLog('info', `${cuenta.email}: ${cuenta.password}`);
        });
      }
      
    } catch (error: any) {
      addLog('error', `💥 Error en migración: ${error.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h3 className="mb-0">🏢 Migración AMPI - Agencias y Miembros</h3>
              <small>Sistema de integración masiva a Firebase</small>
            </div>
            <div className="card-body">
              
              {/* Carga de archivos */}
              <div className="row mb-4">
                <div className="col-md-6">
                  <div className="card">
                    <div className="card-header bg-info text-white">
                      <h5 className="mb-0">📋 Archivo de Agencias</h5>
                    </div>
                    <div className="card-body">
                      <input
                        type="file"
                        className="form-control"
                        accept=".csv"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleFileRead(file, setCsvAgencias);
                            addLog('info', `Archivo de agencias cargado: ${file.name}`);
                          }
                        }}
                        disabled={isRunning}
                      />
                      <small className="text-muted mt-2 d-block">
                        {csvAgencias ? '✅ Archivo cargado' : '⏳ Selecciona archivo CSV'}
                      </small>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="card">
                    <div className="card-header bg-success text-white">
                      <h5 className="mb-0">👥 Archivo de Miembros</h5>
                    </div>
                    <div className="card-body">
                      <input
                        type="file"
                        className="form-control"
                        accept=".csv"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleFileRead(file, setCsvMiembros);
                            addLog('info', `Archivo de miembros cargado: ${file.name}`);
                          }
                        }}
                        disabled={isRunning}
                      />
                      <small className="text-muted mt-2 d-block">
                        {csvMiembros ? '✅ Archivo cargado' : '⏳ Selecciona archivo CSV'}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Botón de ejecución */}
              <div className="text-center mb-4">
                <button
                  className={`btn btn-lg ${isRunning ? 'btn-warning' : 'btn-danger'}`}
                  onClick={ejecutarMigracion}
                  disabled={isRunning || !csvAgencias || !csvMiembros}
                >
                  {isRunning ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Ejecutando Migración...
                    </>
                  ) : (
                    <>
                      🚀 EJECUTAR MIGRACIÓN COMPLETA
                    </>
                  )}
                </button>
                {isRunning && (
                  <div className="mt-2">
                    <small className="text-warning">
                      ⚠️ No cierre esta ventana durante la migración
                    </small>
                  </div>
                )}
              </div>

              {/* Logs en tiempo real */}
              {logs.length > 0 && (
                <div className="card mb-4">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">📋 Logs de Migración</h5>
                    <span className="badge bg-secondary">{logs.length} entradas</span>
                  </div>
                  <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {logs.map((log, index) => {
                      const getLogClass = (type: string) => {
                        switch (type) {
                          case 'success': return 'border-success bg-success bg-opacity-10 text-success';
                          case 'warning': return 'border-warning bg-warning bg-opacity-10 text-warning';
                          case 'error': return 'border-danger bg-danger bg-opacity-10 text-danger';
                          default: return 'border-info bg-info bg-opacity-10 text-info';
                        }
                      };

                      return (
                        <div 
                          key={index} 
                          className={`border-start border-3 p-2 mb-1 ${getLogClass(log.type)}`}
                        >
                          <small className="text-muted d-block mb-1">
                            {log.timestamp.toLocaleTimeString()}
                          </small>
                          <div className="font-monospace small">
                            {log.message}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Resultados finales */}
              {results && (
                <div className="card border-success">
                  <div className="card-header bg-success text-white">
                    <h5 className="mb-0">📊 Resultados de la Migración</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4">
                        <div className="card">
                          <div className="card-header bg-info text-white">
                            <h6 className="mb-0">🏢 Agencias</h6>
                          </div>
                          <div className="card-body">
                            <div className="row text-center">
                              <div className="col-6">
                                <h4 className="text-success">{results.agencias.exitosas}</h4>
                                <small>Exitosas</small>
                              </div>
                              <div className="col-6">
                                <h4 className="text-danger">{results.agencias.fallidas.length}</h4>
                                <small>Fallidas</small>
                              </div>
                            </div>
                            <div className="mt-2">
                              <small className="text-muted">
                                Total procesadas: {results.agencias.total}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div className="card">
                          <div className="card-header bg-success text-white">
                            <h6 className="mb-0">👥 Miembros</h6>
                          </div>
                          <div className="card-body">
                            <div className="row text-center">
                              <div className="col-6">
                                <h4 className="text-success">{results.miembros.exitosos}</h4>
                                <small>Exitosos</small>
                              </div>
                              <div className="col-6">
                                <h4 className="text-danger">{results.miembros.fallidos.length}</h4>
                                <small>Fallidos</small>
                              </div>
                            </div>
                            <div className="mt-2">
                              <small className="text-muted">
                                Total procesados: {results.miembros.total}
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div className="card">
                          <div className="card-header bg-warning text-white">
                            <h6 className="mb-0">🔐 Cuentas Firebase</h6>
                          </div>
                          <div className="card-body">
                            <div className="row text-center">
                              <div className="col-6">
                                <h4 className="text-success">{results.cuentas_firebase.creadas}</h4>
                                <small>Creadas</small>
                              </div>
                              <div className="col-6">
                                <h4 className="text-danger">{results.cuentas_firebase.fallidas.length}</h4>
                                <small>Fallidas</small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Errores detallados */}
                    {(results.agencias.fallidas.length > 0 || 
                      results.miembros.fallidos.length > 0 || 
                      results.cuentas_firebase.fallidas.length > 0) && (
                      <div className="mt-4">
                        <h6 className="text-danger">❌ Errores Detallados:</h6>
                        <div className="accordion" id="errorsAccordion">
                          
                          {results.agencias.fallidas.length > 0 && (
                            <div className="accordion-item">
                              <h2 className="accordion-header">
                                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#agenciasErrors">
                                  Errores en Agencias ({results.agencias.fallidas.length})
                                </button>
                              </h2>
                              <div id="agenciasErrors" className="accordion-collapse collapse" data-bs-parent="#errorsAccordion">
                                <div className="accordion-body">
                                  <pre className="small text-danger">
                                    {JSON.stringify(results.agencias.fallidas, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          )}

                          {results.miembros.fallidos.length > 0 && (
                            <div className="accordion-item">
                              <h2 className="accordion-header">
                                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#miembrosErrors">
                                  Errores en Miembros ({results.miembros.fallidos.length})
                                </button>
                              </h2>
                              <div id="miembrosErrors" className="accordion-collapse collapse" data-bs-parent="#errorsAccordion">
                                <div className="accordion-body">
                                  <pre className="small text-danger">
                                    {JSON.stringify(results.miembros.fallidos, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          )}

                          {results.cuentas_firebase.fallidas.length > 0 && (
                            <div className="accordion-item">
                              <h2 className="accordion-header">
                                <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#firebaseErrors">
                                  Errores en Firebase ({results.cuentas_firebase.fallidas.length})
                                </button>
                              </h2>
                              <div id="firebaseErrors" className="accordion-collapse collapse" data-bs-parent="#errorsAccordion">
                                <div className="accordion-body">
                                  <pre className="small text-danger">
                                    {JSON.stringify(results.cuentas_firebase.fallidas, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}